import React, {Fragment, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import {getEContractClient} from 'js/application/components/signContract/helper';
import {options, __} from 'js/tool/page/current';
import Agreement from 'js/application/components/dataModal/steps/agreement';
import useFormMutation from 'js/reusable/formHook';
import {onlyTrueValidator, setBoolInput, setObjectValue} from 'js/reusable/formHelper';
import publicIp from 'public-ip';
import ErrorBoundary from 'js/reusable/errorBoundary';
import {Link} from 'react-router-dom';
import Spinner from 'js/reusable/loading';
import {GetEContractDataDocument} from 'js/reusable/eContract/__document__/queries';
import {useMutation, useQuery} from '@apollo/client';
import {SignEContractDocument} from 'js/application/helpers/dataModal/__document__/mutations';

const queryClient = getEContractClient('eContracts');
const mutationClient = getEContractClient('signEContract');

function SignContractContainer({eContractHash}) {
	const {data, loading} = useQuery(
		GetEContractDataDocument,
		{
			variables: {filter: {eContractHash}},
			client:    queryClient,
		}
	);
	const [signEContract]       = useMutation(SignEContractDocument, {client: mutationClient});
	const [ip, setIp]           = useState('-');
	const [isSaving, setSaving] = useState(false);
	const [isSigned, setSigned] = useState(false);

	const eContractData = data && data.eContracts && data.eContracts[0] ? data.eContracts[0] : null;

	useEffect(() => {
		(async () => {
			setIp((await publicIp.v4()));
		})();
	}, []);

	const agreementHandler = useFormMutation(
		{
			signature:    {value: null},
			proofFile:    {value: null, valueSetter: setObjectValue},
			didReadTerms: {value: false, valueSetter: setBoolInput, validator: onlyTrueValidator},
		},
		saveAgreement
	);

	function saveAgreement(values, closeCallback) {
		setSaving(true);

		signEContract({variables: {
			eContractHash:  eContractData.eContractHash,
			ip:             ip,
			signatureImage: {file: values.signature},
			repDocument:    {file: values.proofFile},
		}}).catch(() => {
			setSaving(false);
		});

		closeCallback();
	}

	function handleAgreementSave() {
		agreementHandler.handleSubmit(agreementSaved);
	}

	function agreementSaved() {
		setSigned(true);
		setSaving(false);
	}

	return (
		<Container style={{justifyContent: 'center', padding: '50px 0'}}>
			<Row>
				<Col xs={{span: 8, offset: 2}}>
					{
						(!isSigned && eContractData && !eContractData.signedAt) &&
						<Fragment>
							<Card className="sign-contract-card">
								{
									eContractData &&
									<ErrorBoundary
										componentName="Agreement"
									>
										<Agreement
											handler={agreementHandler}
											contractData={eContractData}
											ip={ip}
											isSaving={isSaving}
										/>
									</ErrorBoundary>
								}
							</Card>

							<div className="sign-contract-button-container">
								<Button
									onClick={handleAgreementSave}
									disabled={
										isSaving ||
										loading ||
										Object.keys(agreementHandler.validationErrors).length
									}
								>
									{__('fb:accept')}&nbsp;
									{
										isSaving ?
											<Spinner/> :
											<i className="fas fa-check"/>
									}
								</Button>
							</div>
						</Fragment>
					}

					{
						(isSigned || eContractData && eContractData.signedAt) &&
						<Card className="sign-contract-card signed">
							<i className="fas fa-file-signature signed-icon" />

							<div className="signed-notice-div">
								{__('fb:reg done body')}

								<Link to="/">
									<Button>
										{__('fb:go to login')} <i className="fas fa-angle-right" />
									</Button>
								</Link>
							</div>
						</Card>
					}

					{
						!eContractData && !loading &&
						<Card className="sign-contract-card signed">
							<i className="fas fa-unlink signed-icon" />

							<div className="signed-notice-div">
								{__('fb:contract not found')}
								&nbsp;
								<a
									href={'mailto:' + options.supportEmail.address}
									target="_blank"
								>
									{options.supportEmail.address}
								</a>

								<Link to="/">
									<Button>
										{__('fb:go to login')} <i className="fas fa-angle-right" />
									</Button>
								</Link>
							</div>
						</Card>
					}

					{
						loading &&
						<Card className="sign-contract-card signed">
							<Spinner className="signed-icon" />
						</Card>
					}
				</Col>
			</Row>
		</Container>
	);
}

SignContractContainer.propTypes = {
	eContractHash: PropTypes.string.isRequired,
};

export default SignContractContainer;
